import {
    DropDown,
    getFilterElementsDefault,
    getMaskedPhone,
    getMaskFromName,
    PhoneNumber,
    sharpDateFormatter,
    useErrorState,
    useWorkplaceState,
    WorkplaceBtnActions,
    WorkplaceBtnDelete
} from '@finbeat/react-toolkit';
import { faBoltLightning, faHandPointer } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectList as getBrandSelectList } from 'axios/brandApi';
import { deleteClient, getClientsExcel, getClientsList } from 'axios/clientApi';
import { getSelectList } from 'axios/dictionaryApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { ClientFilter, ClientTable } from 'models/api/client';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import ClientBlocksBtn from './ClientBlocksBtn';
import ClientChangeModal from './ClientChangeModal';
import ClientCrossLinksBtn from './ClientCrossLinksBtn';
import './Clients.scss';
import ClientsFilter from './ClientsFilter';

const Clients = () => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const { dateFormat } = useGeneralSettingsContext();
    const { handleRequestError } = useErrorState();

    const [brands, setBrands] = useState<DropDown[]>([]);
    const [collectionStatuses, setCollectionStatuses] = useState<DropDown[]>([]);
    const [isModalVisible, setModalVisible] = useState(false);
    const [filterChanged, setFilterChanged] = useState(false);
    const [blockModalId, setBlockModalId] = useState('');
    const [crossLinksModalId, setCrossLinksModalId] = useState('');
    const { hasRole } = useAuth();

    const { isFetching } = useQuery('Brands', () => getBrandSelectList(api), {
        onSuccess: (data) => {
            setBrands(data);
        },
        enabled: brands.length === 0,
    });

    const getDictsQuery = useQuery(
        ['clientDicts', i18n.language],
        () => getSelectList(api, ['ClientCollectionStatus']),
        {
            onSuccess: (data) =>
                data?.ClientCollectionStatus
                && setCollectionStatuses(data.ClientCollectionStatus),
            onError: (err) => handleRequestError(err),
        }
    );

    return (
        <BOWorkplace<ClientTable, ClientTable, ClientFilter>
            defaultContent={
                !filterChanged && (
                    <h1 className="text-secondary mx-2">
                        <FontAwesomeIcon icon={faHandPointer} />{' '}
                        <Trans i18nKey="BackOfficeClientSearch" />
                    </h1>
                )
            }
            pageTitle="BackOfficeClients"
            fetchQueryFn={(filter) => getClientsList(api, filter)}
            filterParams={{
                id: '',
                phone: '',
                brandCode: '',
                docNum: '',
                fio: '',
                collectionStatus: null,
                daysOverdue: null
            }}
            initColumns={[
                { key: 'id', name: <Trans i18nKey="BackOfficeClientId" /> },
                { key: 'brandName', name: <Trans i18nKey="BackOfficeBrand" /> },
                {
                    key: 'phoneNumber',
                    name: <Trans i18nKey="BackOfficePhone" />,
                    formatter: ({ row }) => <PhoneNumber value={row.phoneNumber} />,
                },
                { key: 'email', name: <Trans i18nKey="BackOfficeEmail" /> },
                { key: 'fio', name: <Trans i18nKey="BackOfficeFio" /> },
                {
                    key: 'birthday',
                    name: <Trans i18nKey="BackOfficeBirthday" />,
                    formatter: ({ row }) => (
                        <>{sharpDateFormatter(row.birthday, dateFormat || null)}</>
                    ),
                },
                { key: 'docNum', name: <Trans i18nKey="BackOfficeDocNum" /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'base-data-grid__cell_centered no-change-modal',
                    headerCellClass: 'base-data-grid__cell_centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: ({ row }) => (
                        <WorkplaceBtnActions>
                            {hasRole('clientsGetCrossLinks') && (
                                <ClientCrossLinksBtn
                                    key={`client-cross-links-btn-${row.id}`}
                                    id={row.id}
                                    toggle={(item) => setCrossLinksModalId(item)}
                                    isOpen={crossLinksModalId === row.id.toString()}
                                />
                            )}
                            {hasRole('clientsGetBlocks') && (
                                <ClientBlocksBtn
                                    key={`client-blocks-btn-${row.id}`}
                                    id={row.id}
                                    toggle={(item) => setBlockModalId(item)}
                                    isOpen={blockModalId === row.id.toString()}
                                />
                            )}
                            {hasRole('clientsDelete') && (
                                <WorkplaceBtnDelete key={`delete-btn-${row.id}`} id={row.id} />
                            )}
                        </WorkplaceBtnActions>
                    ),
                },
            ]}
            storagePath="clients/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'id', name: t('BackOfficeClientId') },
                {
                    id: 'brandCode',
                    name: t('BackOfficeBrand'),
                    formatter: (val) => brands.find((x) => x.id === val)?.name ?? val,
                },
                {
                    id: 'email',
                    name: t('BackOfficeEmail'),
                },
                {
                    id: 'phone',
                    name: t('BackOfficePhone'),
                    formatter: (val) =>
                        getMaskedPhone(val?.toString(), getMaskFromName('Uzbekistan')),
                },
                { id: 'docNum', name: t('BackOfficeDocNum') },
                { id: 'fio', name: t('BackOfficeFio') },
            ])}
            deleteMutationFn={(id) => deleteClient(api, id)}
            fetchExcelQueryFn={(filter) => getClientsExcel(api, filter)}
            queryKey={['clients']}
            queryClient={queryClient}
            filter={
                <ClientsFilter
                    brands={brands}
                    collectionStatuses={collectionStatuses}
                    getDictsQuery={getDictsQuery}
                />
            }
            onFilterApply={() => setFilterChanged(true)}
            loadingFilter={isFetching}
            showModal={hasRole('clientsGetById') ? () => setModalVisible(true) : undefined}
            modal={{
                component: isModalVisible && (
                    <ClientChangeModal isOpen={isModalVisible} toggle={setModalVisible} />
                ),
            }}
            rowClass={(row) => (row.isBlocked ? 'clients__blocked-client-row' : undefined)}
            showExcelBtn={hasRole('clientsExcel')}
        />
    );
};

const useClientWorkplaceState = () => useWorkplaceState<ClientTable, ClientTable>();

export default Clients;
export { useClientWorkplaceState };
